import { useCallback, useEffect } from "react";
import { debugLog } from "../utils/debugLog";

const BACKEND_URL = window.location.origin;

export function useMessageReactions({ ably, matchId, token, onUpdate }) {
  // Subscribe to reaction updates
  useEffect(() => {
    if (!ably || !matchId) return;
    
    const channel = ably.channels.get(`chat:match:${matchId}`);
    
    // Add safe error handlers to prevent crashes
    const safeErrorHandler = (err) => {
      console.error('[Reactions] Channel error:', err);
      // DO NOT throw
    };
    const safeDetachedHandler = (stateChange) => {
      console.warn('[Reactions] Channel detached:', stateChange?.reason || '');
      // DO NOT throw - normal lifecycle
    };
    
    channel.off('error', safeErrorHandler);
    channel.off('detached', safeDetachedHandler);
    channel.on('error', safeErrorHandler);
    channel.on('detached', safeDetachedHandler);
    
    const handleReactionUpdate = (message) => {
      const data = message.data;
      
      // Ticket 21: Strict validation before updating
      if (data.type === 'reaction_update' && onUpdate) {
        const { message_id, reactions } = data;
        
        // Ticket 21: Prevent stale or invalid updates
        if (!message_id || typeof reactions !== 'object') {
          debugLog('reactions', 'Invalid reaction update received', data);
          return;
        }
        
        debugLog('reactions', 'Apply reaction_update', {
          message_id,
          emoji: data.emoji,
          action: data.action,
          numReactions: Object.keys(reactions).length,
        });
        
        onUpdate(data);
      }
    };
    
    channel.subscribe("reaction_update", handleReactionUpdate);
    
    return () => {
      channel.unsubscribe("reaction_update", handleReactionUpdate);
    };
  }, [ably, matchId, onUpdate]);

  // Function to toggle reaction (calls backend API)
  const toggleReaction = useCallback(
    async (messageId, emoji) => {
      if (!token || !matchId) return null;

      const normalizedMessageId = String(messageId);

      try {
        const res = await fetch(
          `${BACKEND_URL}/api/chat/${matchId}/messages/${normalizedMessageId}/react`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ emoji }),
          }
        );

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          console.error("Failed to toggle reaction:", data || (await res.text()));
          return null;
        }

        // ✅ Force immediate UI update (no Ably dependency)
        const reactions =
          data && data.reactions && typeof data.reactions === "object" && !Array.isArray(data.reactions)
            ? data.reactions
            : {};

        onUpdate?.({
          type: "reaction_update",
          message_id: normalizedMessageId, // ✅ Always provide it
          emoji,
          action: data?.action || "updated",
          reactions,
        });

        return data;
      } catch (error) {
        console.error("Error toggling reaction:", error);
        return null;
      }
    },
    [matchId, token, onUpdate]
  );

  return { toggleReaction };
}
